import React, { memo, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { noop, isUndefined } from 'lodash-es';

import './view-more.scss';

/**
 * ViewMore - A collapsible/expandable component that toggles between collapsed and expanded states
 *
 * Uses HTML `<details>` and `<summary>` elements to provide accessible expand/collapse functionality.
 * Supports both controlled and uncontrolled modes, with customizable summary text for each state
 * and optional tracking integration.
 *
 * @param {Object} props - Component props
 * @param {React.Node} props.expandedSummary - Text/content shown when component is expanded
 * @param {React.Node} props.collapsedSummary - Text/content shown when component is collapsed
 * @param {React.Node} props.children - Content to show/hide when toggling
 * @param {boolean} [props.open] - Controlled state for open/closed (undefined = uncontrolled)
 * @param {Function} [props.onToggle] - Callback fired when toggling state
 * @param {string} [props.className] - Additional CSS classes for the summary element
 * @param {string} [props.trackingId] - Tracking identifier for analytics
 * @param {string} [props.trackingValue] - Tracking value for analytics
 * @param {string} [props.ariaLabel] - Accessibility label for screen readers
 * @param {string} [props.summaryContainerTag] - HTML tag for summary container (default: 'span')
 * @param {string} [props.summaryContainerClassName] - CSS classes for summary container
 * @param {boolean} [props.btnIgnoreTrackingValue] - Whether to ignore tracking values
 * @param {boolean} [props.noIcon] - Whether to hide the expand/collapse icon
 * @param {string} [props.iconClasses] - CSS classes for the expand/collapse icon
 */
export const ViewMore = memo(
  ({
    expandedSummary,
    collapsedSummary,
    className = '',
    children,
    open: openProp = undefined,
    onToggle = noop,
    trackingId = 'extra_links_show_all',
    trackingValue = '',
    ariaLabel = '',
    summaryContainerTag: SummaryContainerTag = 'span',
    summaryContainerClassName = null,
    btnIgnoreTrackingValue = false,
    noIcon = false,
    iconClasses = 'medium text-primary-darker',
  }) => {
    const [open, setOpen] = useState(isUndefined(openProp) ? false : openProp);

    useEffect(() => {
      setOpen(openProp);
    }, [openProp]);

    const handleToggle = useCallback(
      event => {
        onToggle(!open);
        // Set local state only when component is uncontrolled
        if (isUndefined(openProp)) {
          setOpen(!open);
        }
        event.preventDefault();
      },
      [onToggle, open, openProp]
    );

    return (
      <details className="view-more" open={open}>
        <summary
          className={classnames(
            'px-0 py-0 small text-primary-darker d-flex size-16',
            { 'align-items-center': !className.includes('align-items-') },
            className
          )}
          data-tracking-id={trackingId}
          data-tracking-value={trackingValue}
          onClick={handleToggle}
          {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
          {...(btnIgnoreTrackingValue ? { 'data-tracking-ignore-value': true } : {})}
        >
          <SummaryContainerTag className={classnames(summaryContainerClassName, { 'd-none': !open })}>
            {expandedSummary}
          </SummaryContainerTag>
          <SummaryContainerTag className={classnames(summaryContainerClassName, { 'd-none': open })}>
            {collapsedSummary}
          </SummaryContainerTag>
          {!noIcon && (
            <i
              className={classnames(iconClasses, 'ms-0_25', {
                'icon-arrow-down4': !open,
                'icon-arrow-up4': open,
              })}
              aria-hidden
            />
          )}
        </summary>
        {children}
      </details>
    );
  }
);

ViewMore.propTypes = {
  expandedSummary: PropTypes.node.isRequired,
  collapsedSummary: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  trackingId: PropTypes.string,
  trackingValue: PropTypes.string,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
  onToggle: PropTypes.func,
  open: PropTypes.bool,
  summaryContainerTag: PropTypes.string,
  summaryContainerClassName: PropTypes.string,
  btnIgnoreTrackingValue: PropTypes.bool,
  noIcon: PropTypes.bool,
  iconClasses: PropTypes.string,
};

ViewMore.displayName = 'ViewMore';
